import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { acceptInvite } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [u, setU] = useState("");
  const [em, setEm] = useState("");
  const [p, setP] = useState("");
  const [p2, setP2] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        setInfo(data);
      } catch (e) {
        setErr(formatApiError(e));
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (p !== p2) return setErr("Passwords do not match");
    setBusy(true);
    try {
      await acceptInvite(token, u, p, em);
      toast.success("Account created");
      nav("/", { replace: true });
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (err && !info) return <CenterCard><div className="text-red-400" data-testid="invite-error">{err}</div></CenterCard>;
  if (!info) return <CenterCard><Loader2 className="h-5 w-5 animate-spin text-zinc-400" /></CenterCard>;

  if (!info.valid) {
    return <CenterCard>
      <div className="text-lg font-semibold mb-1">Invite unavailable</div>
      <div className="text-sm text-zinc-400">{info.used ? "This invite has already been used." : "This invite has expired."}</div>
    </CenterCard>;
  }

  return (
    <div className="min-h-screen blueprint-grid flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <div className="h-10 w-10 border border-red-500/60 bg-red-500/10 flex items-center justify-center">
            <UserPlus className="h-5 w-5 text-red-400" />
          </div>
          <div>
            <div className="text-lg font-semibold">Accept Invite</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
              Role: {info.role} · From: {info.created_by_username}
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="border border-zinc-800 bg-zinc-950/80 p-6 space-y-4" data-testid="invite-form">
          <Field label="Choose a username" value={u} onChange={setU} testId="invite-username" />
          <Field label="Email address" value={em} onChange={setEm} type="email" testId="invite-email" />
          <Field label="Password" value={p} onChange={setP} type="password" testId="invite-password" />
          <Field label="Confirm password" value={p2} onChange={setP2} type="password" testId="invite-password2" />
          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2" data-testid="invite-form-error">{err}</div>}
          <button disabled={busy} type="submit" data-testid="invite-submit"
            className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-medium py-2 rounded-sm transition-colors flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create Account
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-[0.1em] text-zinc-400 block mb-2">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 focus:ring-2 focus:ring-red-500/30 rounded-sm px-3 py-2 font-mono text-sm outline-none transition-colors" />
    </div>
  );
}

function CenterCard({ children }) {
  return (
    <div className="min-h-screen blueprint-grid flex items-center justify-center px-4">
      <div className="border border-zinc-800 bg-zinc-950/80 p-8 max-w-md w-full text-center">{children}</div>
    </div>
  );
}
